import * as Sentry from '@sentry/browser';
import { useQuery } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import { canUseAI } from '../../api/agents-client/agent-client';
import type { UseAIResult } from '../../api/agents-client/agent-client.class';
import { useOrganizationId } from '../../helpers/auth-helpers/auth.hooks';
import { isValidAccessToken } from '../../helpers/storage/storage';
import { useResizableWidth } from '../../hooks/useResizableWidth';
import { useGlobalStore } from '../../store/global-store/global-store';
import {
  setFloraEffort as setFloraEffortInGlobalStore,
  setFloraIntegrationStatuses as setFloraIntegrationStatusesInGlobalStore,
  setFloraModel as setFloraModelInGlobalStore,
  setFloraModelsAvailable as setFloraModelsAvailableInGlobalStore,
  setFloraVersion as setFloraVersionInGlobalStore,
  setFloraVersionsAvailable as setFloraVersionsAvailableInGlobalStore,
  setUseAI as setUseAIInGlobalStore,
  setUseAIResolved as setUseAIResolvedInGlobalStore,
} from '../../store/global-store/global-store.actions';
import {
  FLORA_ASIDE_MAX_WIDTH_RATIO,
  FLORA_ASIDE_MIN_WIDTH,
  FLORA_ASIDE_RIGHT_GAP,
  type FloraContextualInfo,
  FloraVersion,
} from './flora.type';

interface FloraContextType {
  isFloraVisible: boolean;
  setIsFloraVisible: (isVisible: boolean) => void;
  toggleFlora: () => void;
  askQuestion: (question: string) => void;
  currentExternalQuestion: string | null;
  contextualInfo: FloraContextualInfo | null;
  setContextualInfo: (info: FloraContextualInfo | null) => void;
  setCurrentExternalQuestion: (question: string | null) => void;
  /** Preamble seeded into the first message of a fresh thread (e.g. the current agent session). */
  contextPreamble: string | undefined;
  setContextPreamble: (preamble: string | undefined) => void;
  /** Human-readable label for the surrounding surface, shown in the context bar. */
  contextLabel: string | undefined;
  setContextLabel: (label: string | undefined) => void;
  /** Current width (px) of the Flora aside; defaults to `FLORA_ASIDE_MIN_WIDTH`. */
  floraWidth: number;
  /** Resolved current maximum width (px) for the aside (viewport-relative). */
  floraMaxWidth: number;
  /** Whether the user is actively dragging the aside's resize handle. */
  isResizingFlora: boolean;
  /** Starts a width drag — wire to the resize handle's `onMouseDown`. */
  startFloraResize: (event: ReactMouseEvent) => void;
  /** Imperatively set the aside width (clamped); used for keyboard resizing. */
  setFloraWidth: (width: number | ((previousWidth: number) => number)) => void;
}

const FloraContext = createContext<FloraContextType | undefined>(undefined);

/**
 * Provides the Flora aside's shared state to the authenticated app: visibility,
 * the externally-asked question, the contextual-object summary, the drag-resize
 * width, and the route-scoped context seed.
 *
 * The seed is the non-obvious part. `contextPreamble` and `contextLabel` are
 * stored tagged with the pathname they were seeded for and derived back at
 * render time, so a route change clears them without a second effect racing the
 * one that seeded them — child effects flush before parent effects in the same
 * commit, so an effect-based reset here could wipe a same-commit seed on a
 * warm-cache navigation before any consumer read it. Surfaces seed via
 * `setContextPreamble` / `setContextLabel` (see `session-detail.tsx`); the page
 * viewer passes its own values to `FloraAside` as props instead.
 *
 * @param {ReactNode} children - The subtree that may consume the context.
 * @returns {JSX.Element} The provider wrapping `children`.
 */
export const FloraProvider = ({ children }: { children: ReactNode }) => {
  const orgId = useOrganizationId();
  const location = useLocation();
  const floraVersionsAvailable = useGlobalStore((state) => state.floraVersionsAvailable);
  const floraVersion = useGlobalStore((state) => state.floraVersion);
  const isGlobalStateInitialized = useGlobalStore((state) => state.initialized);
  const [isFloraVisible, setIsFloraVisible] = useState(false);
  const [currentExternalQuestion, setCurrentExternalQuestion] = useState<string | null>(null);
  const [contextualInfo, setContextualInfo] = useState<FloraContextualInfo | null>(null);
  // Tagged with the pathname each was seeded for so route changes clear them
  // via render-time derivation rather than a second effect racing the setter
  // effect that seeded them (child effects flush before parent effects in the
  // same commit, so an effect-based reset here can wipe a same-commit seed on
  // a warm-cache navigation before it is ever read).
  const [contextPreambleSeed, setContextPreambleSeed] = useState<{
    pathname: string;
    value: string | undefined;
  } | null>(null);
  const [contextLabelSeed, setContextLabelSeed] = useState<{ pathname: string; value: string | undefined } | null>(
    null,
  );
  const contextPreamble = contextPreambleSeed?.pathname === location.pathname ? contextPreambleSeed.value : undefined;
  const contextLabel = contextLabelSeed?.pathname === location.pathname ? contextLabelSeed.value : undefined;
  // Updater form, returning `prev` unchanged when nothing moved: a fresh object
  // literal never satisfies useState's `Object.is` bailout, so a redundant seed
  // (same route, byte-identical value) would otherwise commit a provider
  // re-render and pay a full context-propagation walk for no visible change.
  const setContextPreamble = useCallback(
    (value: string | undefined) =>
      setContextPreambleSeed((prev) =>
        prev?.pathname === location.pathname && prev.value === value ? prev : { pathname: location.pathname, value },
      ),
    [location.pathname],
  );
  const setContextLabel = useCallback(
    (value: string | undefined) =>
      setContextLabelSeed((prev) =>
        prev?.pathname === location.pathname && prev.value === value ? prev : { pathname: location.pathname, value },
      ),
    [location.pathname],
  );
  const [lastSelectedFloraVersion, setLastSelectedFloraVersion] = useState<FloraVersion | null>(null);

  // Drag-to-resize width for the right-docked aside. Min = the former fixed
  // width; max = FLORA_ASIDE_MAX_WIDTH_RATIO (40%) of the viewport. Session-only
  // (resets on reload) but lives here so it survives in-app navigation and is
  // shared by the layout shell and the resize handle.
  const {
    width: floraWidth,
    maxWidth: floraMaxWidth,
    isResizing: isResizingFlora,
    handleMouseDown: startFloraResize,
    setWidth: setFloraWidth,
  } = useResizableWidth({
    initialWidth: FLORA_ASIDE_MIN_WIDTH,
    minWidth: FLORA_ASIDE_MIN_WIDTH,
    maxWidth: () => Math.round(window.innerWidth * FLORA_ASIDE_MAX_WIDTH_RATIO),
    computeWidth: (event) => window.innerWidth - FLORA_ASIDE_RIGHT_GAP - event.clientX,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <trigger on pathname change>
  useEffect(() => {
    setContextualInfo(null);
  }, [location.pathname]);

  const toggleFlora = () => {
    setIsFloraVisible(!isFloraVisible);
  };

  const askQuestion = (question: string) => {
    setIsFloraVisible(true);
    setCurrentExternalQuestion(question);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <trigger on floraVersion validity change>
  useEffect(() => {
    const isCurrentVersionAvailable = floraVersionsAvailable.includes(floraVersion);
    const hasAvailableVersions = floraVersionsAvailable.length > 0;

    if (
      lastSelectedFloraVersion &&
      floraVersionsAvailable.includes(lastSelectedFloraVersion) &&
      floraVersion !== lastSelectedFloraVersion
    ) {
      setFloraVersionInGlobalStore(lastSelectedFloraVersion);
      setLastSelectedFloraVersion(null); // Clear immediately after restoring to prevent blocking future changes
      return;
    }

    if (hasAvailableVersions && !isCurrentVersionAvailable) {
      setLastSelectedFloraVersion(floraVersion);
      setFloraVersionInGlobalStore(floraVersionsAvailable[0]);
    }
  }, [floraVersionsAvailable, floraVersion]);

  const canUseAIQuery = useQuery<UseAIResult>({
    queryKey: ['canUseAI', orgId] as const,
    queryFn: async () => {
      const isCurrentOrganization = () => {
        const state = useGlobalStore.getState();
        return (state.organization?.id ?? state.portfolio?.organization?.id) === orgId;
      };
      try {
        const data = await canUseAI(orgId as string);

        if (data && typeof data.canUseAI === 'boolean') {
          if (isCurrentOrganization()) {
            setUseAIInGlobalStore(data.canUseAI);
            setFloraIntegrationStatusesInGlobalStore(
              data.floraIntegrationStatuses || {
                slack: false,
                microsoft_teams: false,
                webex: false,
              },
            );
            setFloraVersionsAvailableInGlobalStore(data.floraVersionsAvailable || [FloraVersion.LANGCHAIN]);

            // Store the super-user model options and reconcile the selection.
            // Reset to the default when there is no selection yet OR the persisted
            // selection is no longer available (e.g. Fable hidden in production,
            // a retired model id, or the user lost super-user access). When the
            // catalog is empty (non-super-user), clear any stale persisted value.
            const modelsAvailable = data.floraModelsAvailable ?? [];
            setFloraModelsAvailableInGlobalStore(modelsAvailable);
            if (modelsAvailable.length > 0) {
              const currentModel = useGlobalStore.getState().floraModel;
              const isCurrentAvailable = modelsAvailable.some((option) => option.model_id === currentModel);
              if (!isCurrentAvailable) {
                const defaultOption = modelsAvailable.find((option) => option.is_default) ?? modelsAvailable[0];
                setFloraModelInGlobalStore(defaultOption.model_id);
                setFloraEffortInGlobalStore(defaultOption.default_effort);
              }
            } else if (useGlobalStore.getState().floraModel) {
              setFloraModelInGlobalStore('');
              setFloraEffortInGlobalStore(null);
            }
          }
        } else {
          Sentry.captureMessage('[Flora] canUseAI returned unexpected data shape', {
            level: 'warning',
            extra: { orgId, data },
          });
        }
        return data;
      } catch (error) {
        if (isCurrentOrganization()) {
          setUseAIInGlobalStore(false);
          setFloraIntegrationStatusesInGlobalStore({
            slack: false,
            microsoft_teams: false,
            webex: false,
          });
          setFloraVersionsAvailableInGlobalStore([FloraVersion.LANGCHAIN]);
        }

        Sentry.captureException(error);
        throw error;
      }
    },
    // Gate on auth + initialized so a stale persisted orgId from a prior session
    // can't fire canUseAI before ProtectedRoute redirects (would produce 401s)
    // or before initGlobalState replaces the persisted scope on cold load.
    enabled: !!orgId && isValidAccessToken() && isGlobalStateInitialized,
    staleTime: 0, // Always fetch fresh data when orgId changes
    refetchOnMount: true, // Ensure refetch on mount to get latest data
  });

  useEffect(() => {
    // A query function runs once per retry. Wait until React Query reports the
    // request fully settled so a transient failure cannot flip Flora to its
    // access-denied UI while a later retry is still in flight.
    if (
      orgId &&
      canUseAIQuery.isEnabled &&
      canUseAIQuery.isFetchedAfterMount &&
      !canUseAIQuery.isFetching &&
      (canUseAIQuery.isSuccess || canUseAIQuery.isError)
    ) {
      setUseAIResolvedInGlobalStore(true, orgId);
    }
  }, [
    orgId,
    canUseAIQuery.isEnabled,
    canUseAIQuery.isError,
    canUseAIQuery.isFetchedAfterMount,
    canUseAIQuery.isFetching,
    canUseAIQuery.isSuccess,
  ]);

  return (
    <FloraContext.Provider
      value={{
        isFloraVisible,
        setIsFloraVisible,
        toggleFlora,
        askQuestion,
        currentExternalQuestion,
        setCurrentExternalQuestion,
        contextualInfo,
        setContextualInfo,
        contextPreamble,
        setContextPreamble,
        contextLabel,
        setContextLabel,
        floraWidth,
        floraMaxWidth,
        isResizingFlora,
        startFloraResize,
        setFloraWidth,
      }}
    >
      {children}
    </FloraContext.Provider>
  );
};

/**
 * Reads the Flora context. Must be called inside a {@link FloraProvider} — it
 * throws rather than returning a partial value, so a consumer mounted outside
 * the provider fails at the call site instead of silently seeing `undefined`
 * for every field.
 *
 * @returns {FloraContextType} The full Flora context value.
 * @throws {Error} When called outside a `FloraProvider`.
 */
export const useFlora = () => {
  const context = useContext(FloraContext);
  if (context === undefined) {
    throw new Error('useFlora must be used within a FloraProvider');
  }
  return context;
};
